import { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  FormLabel,
  FormControl,
  Divider,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Center,
  Tag,
} from '@chakra-ui/react';
import { FiSave, FiGitMerge } from 'react-icons/fi';
import Select from 'react-select';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders } from '../../../utils/DefaultFunction';

const CallFlowNodeProperties = ({ nodeConfig, setNodeConfig, updateNodeData }) => {
  const [flows, setFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);

  useEffect(() => {
    fetchFlows();
  }, []);

  useEffect(() => {
    if (nodeConfig.selectedFlowId && flows.length > 0) {
      const flow = flows.find((f) => (f._id || f.id) === nodeConfig.selectedFlowId);
      if (flow && (!selectedFlow || (selectedFlow._id || selectedFlow.id) !== (flow._id || flow.id))) {
        setSelectedFlow(flow);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows.length, nodeConfig.selectedFlowId]);

  const fetchFlows = async () => {
    setLoadingFlows(true);
    try {
      const response = await API.get(
        `${process.env.REACT_APP_FLOW_API_URL}/flow/list`,
        { params: { page: 1, limit: 100 }, headers: getApiHeaders() }
      );
      setFlows(response.data.flows || []);
    } catch (err) {
      console.error('Failed to fetch flows:', err);
    } finally {
      setLoadingFlows(false);
    }
  };

  const flowOptions = flows
    .filter((f) => f.status === 'active')
    .map((f) => ({
      value: f._id || f.id,
      label: f.name,
      status: f.status,
      flow: f,
    }));

  const handleFlowSelect = (option) => {
    if (!option) {
      setSelectedFlow(null);
      setNodeConfig({
        ...nodeConfig,
        selectedFlowId: '',
        selectedFlowName: '',
        flowStatus: '',
      });
      return;
    }
    setSelectedFlow(option.flow);
    setNodeConfig({
      ...nodeConfig,
      selectedFlowId: option.value,
      selectedFlowName: option.label,
      flowStatus: option.status,
    });
  };

  const currentOption = flowOptions.find(
    (o) => o.value === nodeConfig.selectedFlowId
  ) || null;

  return (
    <VStack spacing={4} align="stretch" mt={4}>
      {/* Step Name */}
      <FormControl>
        <FormLabel fontSize="sm">Step Name</FormLabel>
        <Input
          size="sm"
          value={nodeConfig.label || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
          placeholder="e.g., Call Cart Recovery Flow"
        />
      </FormControl>

      <Divider />

      {/* Flow Selector */}
      <FormControl>
        <FormLabel fontSize="sm">Select Flow to Call</FormLabel>
        {loadingFlows ? (
          <Center py={4}>
            <Spinner size="sm" color="teal.500" />
          </Center>
        ) : (
          <Select
            options={flowOptions}
            value={currentOption}
            onChange={handleFlowSelect}
            placeholder="Search and select a flow..."
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({ ...base, fontSize: '14px', minHeight: '36px' }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            formatOptionLabel={(option) => (
              <HStack spacing={2}>
                <Box w={2} h={2} borderRadius="full" bg="green.400" flexShrink={0} />
                <Text fontSize="sm">{option.label}</Text>
              </HStack>
            )}
            noOptionsMessage={() =>
              flows.length === 0 ? 'No flows found' : 'No active flows match your search'
            }
          />
        )}
        <Text fontSize="xs" color="gray.500" mt={1}>
          Only active flows can be called
        </Text>
      </FormControl>

      {/* Selected Flow Preview */}
      {selectedFlow && (
        <Box p={3} bg="teal.50" border="1px solid" borderColor="teal.200" borderRadius="md">
          <Flex align="center" gap={2} mb={1}>
            <FiGitMerge size={14} color="#0d9488" />
            <Text fontSize="sm" fontWeight="medium" color="teal.800">
              Selected Flow
            </Text>
          </Flex>
          <Text fontSize="sm" color="teal.700">{selectedFlow.name}</Text>
          <Tag
            mt={1.5}
            colorScheme={selectedFlow.status === 'active' ? 'green' : 'gray'}
            variant="subtle"
            fontSize="xs"
          >
            {selectedFlow.status}
          </Tag>
        </Box>
      )}

      {/* Info Alert */}
      <Alert status="info" borderRadius="md" fontSize="xs">
        <AlertIcon boxSize={4} />
        <AlertDescription lineHeight="1.5">
          Triggers the selected flow for the current contact. Useful for modular, reusable flow components.
        </AlertDescription>
      </Alert>

      <Divider />

      <Button
        size="sm"
        colorScheme="teal"
        leftIcon={<FiSave />}
        onClick={updateNodeData}
        isDisabled={!nodeConfig.selectedFlowId}
      >
        Update Node
      </Button>
    </VStack>
  );
};

export default CallFlowNodeProperties;
